import React, { useState, useRef, useMemo, useEffect } from "react";
import axios from "axios";
import ModernRichTextEditor from "@/components/ModernRichTextEditor";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// E-Mail Platzhalter
const EMAIL_PLACEHOLDERS = [
  { key: "vorname", label: "Vorname", description: "Vorname des Kindes" },
  { key: "nachname", label: "Nachname", description: "Nachname des Kindes" },
  { key: "gruppe", label: "Gruppe", description: "Name der Gruppe" },
];

export function EmailTabModern({
  children,
  groups,
  authHeaders,
  authToken,
  toast,
  mailEditorColors,
  onSendComplete, // Callback nach erfolgreichem Versand
  permissions = {}, // Berechtigungen
  isSuperAdmin = false,
}) {
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [filterMode, setFilterMode] = useState("all"); // all, only_new, exclude_new
  const [excludePaused, setExcludePaused] = useState(true); // Pausierende standardmäßig ausgeschlossen
  const [includePaused, setIncludePaused] = useState(false); // Pausierende nur bei aktivem Auswählen einschließen
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendingProgress, setSendingProgress] = useState(""); // Fortschritts-Text
  const [showPreview, setShowPreview] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const editorRef = useRef(null);
  
  // Berechtigung für Vorlagen - prüfe explizit auf true (nicht nur truthy)
  // SuperAdmin hat immer Zugriff, andere brauchen email_templates === true
  const canUseTemplates = isSuperAdmin || (permissions && permissions.email_templates === true);
  
  // Status für Template-Laden
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);

  // Vorlagen laden (nur wenn berechtigt)
  useEffect(() => {
    async function loadTemplates() {
      if (!authToken) return;
      if (!canUseTemplates) {
        setTemplates([]);
        return;
      }
      
      setTemplatesLoading(true);
      setTemplatesError(null);
      
      try {
        const headers = authHeaders();
        const res = await axios.get(`${API_BASE}/api/mail-templates`, headers);
        setTemplates(res.data || []);
      } catch (e) {
        console.error("Fehler beim Laden der Vorlagen:", e);
        setTemplatesError(e?.response?.data?.detail || e?.message || "Unbekannter Fehler");
        setTemplates([]);
      } finally {
        setTemplatesLoading(false);
      }
    }
    loadTemplates();
  }, [authToken, canUseTemplates]);

  // Vorlage übernehmen
  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || "");
    setBody(tpl.html || "");
    setShowTemplates(false);
    toast({ title: `Vorlage "${tpl.name}" übernommen` });
  };

  // Gruppen-Style
  const getGroupStyle = (groupId) => {
    const group = groups?.find((g) => g.id === groupId);
    if (!group) return { bg: "#e5e7eb", text: "#1f2937" };
    const bg = group.color || "#e5e7eb";
    let textColor = "#1f2937";
    try {
      if (bg.startsWith("#") && bg.length === 7) {
        const r = parseInt(bg.slice(1, 3), 16);
        const g = parseInt(bg.slice(3, 5), 16);
        const b = parseInt(bg.slice(5, 7), 16);
        textColor = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
      }
    } catch (e) {}
    
    // Prüfen ob manuelle Textfarbe definiert ist (wie in ChildrenTabModern)
    const presets = window.__groupColorPresets || [];
    const badgeSettings = window.__groupBadgeSettings || { text_color_mode: "auto" };
    const preset = presets.find(p => p.hex === bg);
    
    if (badgeSettings.text_color_mode === "manual" && preset?.text_color) {
      textColor = preset.text_color;
    }
    
    return { 
      bg, 
      text: textColor,
      fontSize: "var(--group-badge-font-size)",
      fontWeight: "var(--group-badge-font-weight)"
    };
  };

  // Empfänger berechnen - NUR wenn Gruppen ausgewählt sind
  const recipients = useMemo(() => {
    // Keine Auswahl = keine Empfänger
    if (selectedGroupIds.length === 0) {
      return [];
    }
    
    let list = (children || []).filter((c) => c.status !== "archiviert");
    
    // Gruppen-Filter
    list = list.filter((c) => selectedGroupIds.includes(c.group_id));
    
    // Pausierende Kinder nur einschließen wenn explizit gewählt
    if (!includePaused) {
      list = list.filter((c) => {
        if (!c.pause_until) return true;
        try {
          const pauseDate = new Date(c.pause_until);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return pauseDate < today;
        } catch {
          return true;
        }
      });
    }
    
    // "NEU" Filter - konsistent mit Backend-Logik
    // Kind ist NEU wenn: is_new=true UND keine Mitgliedsnummer (mnr)
    if (filterMode !== "all") {
      list = list.filter((c) => {
        const hasNeuFlag = c.is_new === true;
        const hasMnr = (c.mnr || "").trim() !== "";
        const isNeu = hasNeuFlag && !hasMnr;
        
        if (filterMode === "only_new") return isNeu;
        if (filterMode === "exclude_new") return !isNeu;
        return true;
      });
    }
    
    // Nur Kinder mit E-Mail (unterstützt beide Feldnamen-Varianten)
    return list.filter((c) => c.email || c.email1 || c.secondary_email || c.email2);
  }, [children, groups, selectedGroupIds, filterMode, includePaused]);

  // Anzahl pausierter Kinder in ausgewählten Gruppen (für Info-Anzeige)
  const pausedCount = useMemo(() => {
    if (selectedGroupIds.length === 0) return 0;
    
    let list = (children || []).filter((c) => 
      c.status !== "archiviert" && 
      selectedGroupIds.includes(c.group_id) &&
      (c.email || c.email1 || c.secondary_email || c.email2)
    );
    
    return list.filter((c) => {
      if (!c.pause_until) return false;
      try {
        const pauseDate = new Date(c.pause_until);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return pauseDate >= today;
      } catch {
        return false;
      }
    }).length;
  }, [children, selectedGroupIds]);

  // Anzahl der Empfänger (Kinder mit E-Mail) - jedes Kind bekommt eine personalisierte Mail
  const recipientCount = recipients.length;

  // Alle Gruppen auswählen/abwählen
  const toggleAllGroups = () => {
    if (selectedGroupIds.length === groups?.length) {
      setSelectedGroupIds([]);
    } else {
      setSelectedGroupIds(groups?.map((g) => g.id) || []);
    }
  };

  // Gruppe toggle
  const toggleGroup = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  // E-Mail senden
  const handleSend = async () => {
    if (!subject.trim()) {
      toast({ title: "Bitte Betreff eingeben", variant: "destructive" });
      return;
    }
    if (!body.trim() || body === "<p></p>") {
      toast({ title: "Bitte E-Mail-Text eingeben", variant: "destructive" });
      return;
    }
    if (recipients.length === 0) {
      toast({ title: "Keine Empfänger ausgewählt", variant: "destructive" });
      return;
    }

    setIsSending(true);
    setSendingProgress(`Sende E-Mails an ${recipientCount} Kinder... Dies kann einige Minuten dauern.`);
    
    try {
      const result = await axios.post(
        `${API_BASE}/api/email/group`,
        {
          group_ids: selectedGroupIds.length > 0 ? selectedGroupIds : groups?.map((g) => g.id),
          subject,
          body,
          filter_mode: filterMode,
          exclude_paused: !includePaused,  // Ausschließen wenn nicht explizit eingeschlossen
        },
        {
          ...authHeaders(),
          timeout: 300000, // 5 Minuten Timeout für große E-Mail-Mengen
        }
      );
      
      // Erfolg: Formular zurücksetzen
      setSubject("");
      setBody("");
      setSelectedGroupIds([]);
      setFilterMode("all");
      setShowPreview(false);
      setShowTemplates(false);
      
      // Erfolgs-Toast mit Details
      const sentCount = result.data?.sent_count || recipientCount;
      const failedCount = result.data?.failed_count || 0;
      const failedList = result.data?.failed_recipients || [];
      
      if (failedCount > 0) {
        // Teilerfolg - einige E-Mails fehlgeschlagen
        const failedNames = failedList.map(f => `${f.child_name} (${f.email})`).join("\n");
        toast({ 
          title: `${sentCount} gesendet, ${failedCount} fehlgeschlagen!`, 
          description: `Fehlgeschlagen:\n${failedNames}`,
          variant: "destructive",
          duration: 15000
        });
        // Fehler auch als Alert anzeigen
        setTimeout(() => {
          alert(
            `⚠️ E-Mail-Versand teilweise fehlgeschlagen!\n\n` +
            `✅ Erfolgreich: ${sentCount}\n` +
            `❌ Fehlgeschlagen: ${failedCount}\n\n` +
            `Betroffene Empfänger:\n${failedNames}`
          );
        }, 500);
      } else {
        toast({ 
          title: "E-Mails erfolgreich gesendet!", 
          description: `Alle ${sentCount} E-Mails wurden versendet.`,
          duration: 5000
        });
      }
      
      // Callback aufrufen um Sendeprotokolle zu aktualisieren
      if (onSendComplete) {
        onSendComplete();
      }
    } catch (e) {
      console.error("E-Mail Versand Fehler:", e);
      
      // Prüfen ob es ein Timeout war
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        toast({ 
          title: "Zeitüberschreitung", 
          description: "Der Versand dauert länger als erwartet. Bitte prüfen Sie die Sendeprotokolle - die E-Mails werden möglicherweise noch im Hintergrund versendet.",
          variant: "destructive",
          duration: 10000
        });
      } else {
        const errorMsg = e?.response?.data?.detail || "Bitte prüfen Sie die Sendeprotokolle.";
        toast({ 
          title: "Fehler beim Senden", 
          description: errorMsg,
          variant: "destructive" 
        });
      }
    } finally {
      setIsSending(false);
      setSendingProgress("");
    }
  };

  // Vorschau mit ersetzten Platzhaltern
  const previewContent = useMemo(() => {
    if (!showPreview || recipients.length === 0) return null;
    const sampleChild = recipients[0];
    const sampleGroup = groups?.find((g) => g.id === sampleChild?.group_id);
    
    let previewBody = body
      .replace(/\{\{vorname\}\}/g, sampleChild?.first_name || "Max")
      .replace(/\{\{nachname\}\}/g, sampleChild?.last_name || "Mustermann")
      .replace(/\{\{gruppe\}\}/g, sampleGroup?.name || "Gruppe");
    
    let previewSubject = subject
      .replace(/\{\{vorname\}\}/g, sampleChild?.first_name || "Max")
      .replace(/\{\{nachname\}\}/g, sampleChild?.last_name || "Mustermann")
      .replace(/\{\{gruppe\}\}/g, sampleGroup?.name || "Gruppe");
    
    return { subject: previewSubject, body: previewBody, child: sampleChild };
  }, [showPreview, body, subject, recipients, groups]);

  return (
    <div className="space-y-4">
      {/* Header mit Statistik */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white p-4 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">E-Mail versenden</h2>
          <p className="text-sm text-slate-500">Personalisierte E-Mails an Gruppen senden</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-violet-600">{recipients.length}</div>
            <div className="text-xs text-slate-500">Kinder</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-600">{recipientCount}</div>
            <div className="text-xs text-slate-500">E-Mail-Adressen</div>
          </div>
        </div>
      </div>

      {/* Empfänger-Auswahl */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-lg font-semibold text-slate-900">Empfänger</h3>
        
        {/* Gruppen-Buttons */}
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            onClick={toggleAllGroups}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
              selectedGroupIds.length === groups?.length
                ? "bg-slate-900 text-white"
                : "bg-slate-200 text-slate-500 opacity-40 hover:opacity-70"
            }`}
          >
            Alle Gruppen
          </button>
          {groups?.map((g) => {
            const style = getGroupStyle(g.id);
            const isSelected = selectedGroupIds.includes(g.id);
            // Zähle Kinder mit E-Mail (unterstützt beide Feldnamen-Varianten)
            const count = (children || []).filter(
              (c) => c.group_id === g.id && c.status !== "archiviert" && (c.email || c.email1 || c.secondary_email || c.email2)
            ).length;
            return (
              <button
                key={g.id}
                onClick={() => toggleGroup(g.id)}
                className={`rounded-full px-4 py-2 transition-all ${
                  isSelected ? "" : "opacity-40 hover:opacity-70"
                }`}
                style={{ 
                  backgroundColor: style.bg, 
                  color: style.text,
                  fontSize: style.fontSize,
                  fontWeight: style.fontWeight
                }}
              >
                {g.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Filter für "NEU" (nur relevant für Gruppe 1) */}
        <div className="rounded-lg bg-slate-50 p-3">
          <div className="mb-2 text-sm font-medium text-slate-600">Filter für Gruppe 1:</div>
          <div className="flex flex-wrap gap-3">
            {[
              { value: "all", label: "Alle Kinder" },
              { value: "only_new", label: 'Nur mit "NEU"' },
              { value: "exclude_new", label: '"NEU" ausschließen' },
            ].map((option) => (
              <label key={option.value} className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="filter-mode"
                  value={option.value}
                  checked={filterMode === option.value}
                  onChange={() => setFilterMode(option.value)}
                  className="h-4 w-4 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-xs text-slate-700">{option.label}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            Diese Filterung wirkt nur auf Kinder in Gruppe 1. In anderen Gruppen werden immer alle Kinder angeschrieben.
          </p>
        </div>

        {/* Pausierende Kinder einschließen */}
        {pausedCount > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includePaused}
                onChange={(e) => setIncludePaused(e.target.checked)}
                className="h-4 w-4 rounded text-amber-600 focus:ring-amber-500"
              />
              <span className="text-sm font-medium text-amber-800">Pausierende Kinder einschließen</span>
            </label>
            <p className="mt-1.5 text-xs text-amber-700">
              {includePaused ? (
                <>ℹ️ <strong>{pausedCount}</strong> pausierende{pausedCount === 1 ? 's Kind wird' : ' Kinder werden'} mit angeschrieben.</>
              ) : (
                <>⚠️ <strong>{pausedCount}</strong> pausierende{pausedCount === 1 ? 's Kind wird' : ' Kinder werden'} <strong>nicht</strong> angeschrieben.</>
              )}
            </p>
          </div>
        )}
      </div>

      {/* E-Mail Editor */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Nachricht</h3>
          {canUseTemplates && templates.length > 0 && (
            <button
              type="button"
              onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-200"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Vorlage verwenden
            </button>
          )}
        </div>

        {/* Vorlagen-Auswahl */}
        {canUseTemplates && showTemplates && templates.length > 0 && (
          <div className="mb-4 rounded-lg bg-amber-50 p-3">
            <div className="mb-2 text-xs font-medium text-amber-700">Vorlage auswählen:</div>
            <div className="flex flex-wrap gap-2">
              {templates.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => applyTemplate(tpl)}
                  className="rounded-lg bg-white px-3 py-2 text-left shadow-sm hover:bg-amber-100 transition-all"
                >
                  <div className="text-sm font-medium text-slate-900">{tpl.name}</div>
                  <div className="text-xs text-slate-500 truncate max-w-[200px]">{tpl.subject}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Betreff */}
        <div className="mb-4">
          <label className="mb-1 block text-xs text-slate-500">Betreff</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="E-Mail Betreff..."
            className="w-full rounded-lg border-0 bg-slate-100 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <p className="mt-1 text-[10px] text-slate-400">
            Platzhalter: {"{{vorname}}"}, {"{{nachname}}"}, {"{{gruppe}}"}
          </p>
        </div>

        {/* Rich Text Editor */}
        <div className="mb-4">
          <label className="mb-1 block text-xs text-slate-500">Nachricht</label>
          <ModernRichTextEditor
            ref={editorRef}
            value={body}
            onChange={setBody}
            placeholder="E-Mail Text eingeben..."
            colors={mailEditorColors || ["#000000", "#1f2937", "#0f766e", "#2563eb", "#b91c1c"]}
            placeholders={EMAIL_PLACEHOLDERS}
            showPlaceholders={true}
            minHeight="250px"
          />
        </div>

        {/* Aktions-Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            disabled={isSending}
            className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50"
          >
            {showPreview ? "Vorschau ausblenden" : "Vorschau anzeigen"}
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || recipients.length === 0}
            className="rounded-full bg-emerald-500 px-6 py-2 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-50"
          >
            {isSending ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {sendingProgress || "Wird gesendet..."}
              </span>
            ) : (
              `E-Mails senden (${recipientCount})`
            )}
          </button>
        </div>
        
        {/* Fortschrittsanzeige während des Versands */}
        {isSending && (
          <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-6 w-6 animate-spin rounded-full border-3 border-emerald-500 border-t-transparent" />
              <div>
                <p className="text-sm font-medium text-emerald-800">E-Mails werden versendet...</p>
                <p className="text-xs text-emerald-600">{sendingProgress}</p>
              </div>
            </div>
            <p className="mt-2 text-xs text-emerald-700">
              Bitte warten Sie, bis der Versand abgeschlossen ist. Dies kann je nach Anzahl der Empfänger einen Moment dauern.
            </p>
          </div>
        )}
      </div>

      {/* Vorschau */}
      {showPreview && previewContent && (
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-violet-900">Vorschau</h3>
            <span className="text-xs text-violet-600">
              Beispiel für: {previewContent.child?.first_name} {previewContent.child?.last_name}
            </span>
          </div>
          <div className="rounded-lg bg-white p-4 shadow-inner">
            <div className="mb-3 border-b border-slate-100 pb-3">
              <span className="text-xs text-slate-500">Betreff:</span>
              <div className="font-medium text-slate-900">{previewContent.subject}</div>
            </div>
            <div 
              className="prose prose-sm max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: previewContent.body }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
